import { lazy, Suspense } from 'react';
import { Skeleton } from '../components/skeleton.jsx';
import { cn } from '../lib/utils.js';

const TrendChartImpl = lazy(() => import('./trend-chart-impl.jsx'));

/**
 * @typedef {{ key: string, label: string, color: string }} TrendSeries  color = CSS token, e.g. 'var(--chart-1)'
 * @typedef {{
 *   title: string,
 *   description?: import('react').ReactNode,
 *   data: Array<{ label: string } & Record<string, number|string|null>>,
 *   series: TrendSeries[],
 *   formatValue: (value: number|null) => string,
 *   formatAxis?: (value: number) => string,
 *   height?: number,
 *   className?: string,
 * }} TrendChartProps
 */

/**
 * Line chart over months/years. Only one unit per chart (never a second y-axis) – EUR and % go
 * into separate charts. One series: the title names it (no legend); ≥ 2 series: legend with line
 * keys. Always pair it with a table showing the same values (accessibility, docs/frontend/portal.md §3).
 * @param {TrendChartProps} props
 */
export function TrendChart({ title, description, className, height = 220, ...props }) {
  const { series } = props;
  return (
    <figure data-slot="chart-card" className={cn('flex min-w-0 flex-col gap-2 rounded-lg border bg-card p-4', className)}>
      <figcaption className="flex flex-col gap-0.5">
        <span className="text-sm font-semibold">{title}</span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
        <span className="sr-only">Diagramm; die Werte stehen zusätzlich in der Tabelle.</span>
      </figcaption>
      {series.length > 1 && (
        <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground" aria-label={`Legende: ${title}`}>
          {series.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              <span aria-hidden="true" className="inline-block h-0.5 w-4 rounded-full" style={{ background: s.color }} />
              {s.label}
            </li>
          ))}
        </ul>
      )}
      <div style={{ height }}>
        <Suspense fallback={<Skeleton className="h-full w-full" />}>
          <TrendChartImpl height={height} title={title} {...props} />
        </Suspense>
      </div>
    </figure>
  );
}
